import React, { FC } from "react";
import {
  DataGrid as MuiDataGrid,
  DataGridProps as MuiDataGridProps,
  GridToolbarContainer,
  GridToolbarExport,
  GridSlotsComponent,
  GridValidRowModel,
  GridRowsProp,
  GridColDef,
} from "@mui/x-data-grid";
import { styled } from "@mui/system";
import { GridInitialStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";
import {
  COLORS,
  FONTS,
  FONT_WEIGHTS,
  RADII,
  SPACINGS,
} from "../../common/consts";
import { TableSkeleton } from "./Skeletons";

type PageSizeOption = number | { value: number; label: string };
type PageSizeOptionsType = readonly PageSizeOption[] | undefined;
type SlotsType = Partial<GridSlotsComponent> | undefined;

export type DataGridCol<T extends GridValidRowModel> = GridColDef<T>;
export type DataGridRow<T extends GridValidRowModel> = GridRowsProp<T>[0];

export interface DataGridProps<T extends GridValidRowModel>
  extends MuiDataGridProps {
  columns: DataGridCol<T>[];
  rows: DataGridRow<T>[];
  /** Placeholder rows drawn while the first page is still loading. */
  skeletonRows?: number;
}

const DataGridContainer = styled("div")`
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .MuiDataGrid-root {
    border: none !important;
    min-width: 0;
    /* MUI DataGrid v7 drives the header row / pinned sections from these vars.
       Header uses the SAME surface as the body (both themes) — a tinted
       header band read as a bug in light and dark mode alike. */
    --DataGrid-containerBackground: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}));
    --DataGrid-pinnedBackground: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}));
    --DataGrid-rowBorderColor: var(--color-borderDark, ${COLORS.borderDark});
    background-color: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}));
    .MuiDataGrid-columnHeaderRow,
    .MuiDataGrid-columnHeaders,
    .MuiDataGrid-columnHeader {
      background-color: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary})) !important;
    }
    .MuiDataGrid-virtualScroller,
    .MuiDataGrid-filler,
    .MuiDataGrid-scrollbarFiller {
      background-color: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary})) !important;
    }
    .MuiDataGrid-main {
      border: none !important;
    }
    .MuiDataGrid-container--top {
      border: none !important;
    }
    .MuiDataGrid-container--bottom {
      border: none !important;
    }
    .MuiDataGrid-cell {
      border: none !important;
      border-bottom: 1px solid var(--color-borderDark, ${COLORS.borderDark}) !important;
      display: inline-flex;
      color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
      font: ${FONTS.sm};
      align-items: center;
      justify-content: flex-start;
    }
    /* Outreach-style row hover — subtle surface lift across the whole row */
    .MuiDataGrid-row:hover {
      background-color: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight}) !important;
    }
    .MuiDataGrid-row.Mui-selected,
    .MuiDataGrid-row.Mui-selected:hover {
      background-color: var(--color-backgroundBrandLight, ${COLORS.backgroundBrandLight}) !important;
    }
    .MuiDataGrid-cell[data-field="isWhitelisted"],
    .MuiDataGrid-cell[data-field="isActive"] {
      justify-content: center !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      text-align: center !important;
    }
    .MuiDataGrid-cell,
    .MuiDataGrid-cell:focus,
    .MuiDataGrid-cell:focus-visible,
    .MuiDataGrid-cell:active,
    .MuiDataGrid-cell:focus:active {
      outline: none !important;
      box-shadow: none !important;
      border: none !important;
    }
    /* Exclude toggle label and its children from the general box-shadow removal */
    .MuiDataGrid-cell *:not(.toggle-label):not(.toggle-label span):not(.toggle-label input),
    .MuiDataGrid-cell *:focus:not(.toggle-label):not(.toggle-label span):not(.toggle-label input),
    .MuiDataGrid-cell *:focus-visible:not(.toggle-label):not(.toggle-label span):not(.toggle-label input),
    .MuiDataGrid-cell *:active:not(.toggle-label):not(.toggle-label span):not(.toggle-label input) {
      outline: none !important;
      box-shadow: none !important;
      border: none !important;
    }
    /* Explicitly allow toggle hover box-shadow - this must come after the rule above */
    .MuiDataGrid-cell .toggle-label:hover span:first-of-type {
      box-shadow: 0 0 0 3px rgba(120, 113, 246, 0.15) !important;
    }
    .MuiDataGrid-columnHeader {
      display: inline-flex;
      background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}) !important;
      color: var(--color-foregroundPrimary, var(--color-foregroundBrandTable, ${COLORS.foregroundBrandTable}));
      font: ${FONTS.sm};
      font-weight: ${FONT_WEIGHTS.semibold};
      font-size: 0.813rem;
      align-items: center;
      justify-content: flex-start;
      outline: none !important;
      border: none !important;
      border-bottom: 1px solid var(--color-borderDark, ${COLORS.borderDark}) !important;
      padding: 8px 0 16px 0 !important;
    }
    .MuiDataGrid-columnHeader:focus,
    .MuiDataGrid-columnHeader:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }
    .MuiDataGrid-columnHeaderTitle {
      white-space: normal;
      word-wrap: break-word;
    }
    .MuiDataGrid-sortIcon {
      color: var(--color-foregroundBrandLight, ${COLORS.foregroundBrandLight});
    }
    .MuiDataGrid-columnHeader:hover {
      color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
    }
    .MuiDataGrid-columnHeader:focus {
      outline: none;
      color: var(--color-foregroundBrandTable, ${COLORS.foregroundBrandTable});
    }
    .MuiDataGrid-footerContainer {
      background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary});
      border-top: 1px solid var(--color-borderDark, ${COLORS.borderDark}) !important;
      border-radius: 0;
    }
    .MuiTablePagination-root {
      color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
    }
    .MuiTablePagination-displayedRows {
      font: ${FONTS.md};
      font-weight: ${FONT_WEIGHTS.light};
      font-size: 0.875rem;
      margin-bottom: 0;
    }
    .MuiTablePagination-selectLabel,
    .MuiTablePagination-input {
      display: none !important;
    }
    .MuiTablePagination-actions {
      gap: 8px;
      .MuiIconButton-root {
        position: relative;
        margin: 0 4px;
        width: 48px !important;
        height: 48px !important;
        .MuiSvgIcon-root {
          display: none !important;
        }
        &::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 16px;
          height: 16px;
          /* Drawn as a mask so the chevron follows the theme foreground colour
             instead of the baked-in stroke of a data-URI image. */
          background-color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
          mask-size: contain;
          mask-repeat: no-repeat;
          mask-position: center;
          -webkit-mask-size: contain;
          -webkit-mask-repeat: no-repeat;
          -webkit-mask-position: center;
        }
        &:first-of-type::after {
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M15 18l-6-6 6-6' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M15 18l-6-6 6-6' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        }
        &:last-of-type::after {
          mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M9 18l6-6-6-6' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M9 18l6-6-6-6' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        }
        &:hover {
          background-color: var(--color-backgroundSecondary, rgba(0, 0, 0, 0.04)) !important;
          border-radius: 50% !important;
        }
      }
    }
    .MuiDataGrid-row {
      background-color: var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary})) !important;
      border: none !important;
      min-height: 72px !important;
    }
    .MuiDataGrid-row:hover {
      background-color: var(--color-backgroundBrandTable, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary})) !important;
    }
    .MuiDataGrid-cell {
      min-height: 72px !important;
      padding: 16px 0 !important;
    }
    .MuiDataGrid-cell[data-field="isWhitelisted"],
    .MuiDataGrid-cell[data-field="isActive"] {
      padding: 16px 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      justify-content: center !important;
    }
    .MuiDataGrid-columnHeader[data-field="isWhitelisted"],
    .MuiDataGrid-columnHeader[data-field="isActive"] {
      justify-content: center !important;
    }
  }
`;

const StyledToolBarContainer = styled(GridToolbarContainer)`
  background-color: var(--color-backgroundPrimary, ${COLORS.backgroundPrimary});
  display: flex;
  justify-content: flex-end;
  padding: ${SPACINGS.xl} ${SPACINGS.xl};
  border-bottom: 1px solid var(--color-borderDark, ${COLORS.borderDark});
  .MuiButton-text {
    color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
    border: none;
    text-transform: capitalize;
    border-radius: ${RADII.xl};
    padding: ${SPACINGS.sm} ${SPACINGS.sm};
    font: ${FONTS.sm};
  }
`;

const CustomToolbar: FC = (props) => {
  return (
    <StyledToolBarContainer {...props}>
      <GridToolbarExport />
    </StyledToolBarContainer>
  );
};

const initialState: GridInitialStateCommunity = {
  pagination: {
    paginationModel: {
      pageSize: 50,
    },
  },
};

const pageSizeOptions: PageSizeOptionsType = [];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const slots: SlotsType = {
  toolbar: CustomToolbar,
};

const DataGrid = <T extends GridValidRowModel>({
  columns,
  rows,
  skeletonRows = 6,
  ...props
}: DataGridProps<T>): JSX.Element => {
  /* Before the first row arrives the grid has nothing to draw but its own
     headers, so the body is filled with bones laid out on the very columns
     the rows will use — same widths, same row height, same rules. */
  if (props.loading && rows.length === 0) {
    return (
      <DataGridContainer>
        <TableSkeleton
          columns={columns.map((column) => ({
            width: column.width,
            flex: column.flex,
            minWidth: column.minWidth,
            headerName: column.headerName,
            align:
              column.align === "center" || (column.width != null && column.width <= 80)
                ? "center"
                : "left",
          }))}
          rows={skeletonRows}
          rowHeight={typeof props.rowHeight === "number" ? props.rowHeight : 72}
          headerHeight={
            typeof props.columnHeaderHeight === "number" ? props.columnHeaderHeight : 56
          }
        />
      </DataGridContainer>
    );
  }

  return (
    <DataGridContainer>
      <MuiDataGrid
        disableColumnFilter
        disableColumnMenu
        disableColumnResize
        hideFooterSelectedRowCount
        disableRowSelectionOnClick
        initialState={initialState}
        pageSizeOptions={pageSizeOptions}
        // slots={slots}
        autoHeight
        getRowHeight={() => 72}
        columns={columns}
        rows={rows}
        {...props}
      />
    </DataGridContainer>
  );
};

export default DataGrid;
